import { useEffect, useRef, useState, useCallback } from 'react';

// Helper function to get readable WebSocket state
function getReadyStateString(ws) {
    if (!ws) return 'UNDEFINED';
    
    const states = {
        0: 'CONNECTING',
        1: 'OPEN',
        2: 'CLOSING',
        3: 'CLOSED'
    };
    
    return states[ws.readyState] || `UNKNOWN (${ws.readyState})`;
}

// Singleton WebSocket instance that will be shared across components
let globalWs = null;
let isConnecting = false;
const listeners = new Map();
const endpointData = new Map();

// Check if the WebSocket server is available
async function checkServerAvailability() {
    try {
        // Health check goes through the server-side proxy (no credentials from client).
        // NOTE: any HTTP response — even 401/4xx from the backend — proves this
        // dev server + proxy are reachable. Only a network-level failure means
        // the server is actually down.
        const response = await fetch('/api/proxy/health', { 
            method: 'GET',
            mode: 'cors',
            cache: 'no-cache',
            headers: {
                'Content-Type': 'application/json'
            },
            signal: AbortSignal.timeout(5000)
        });
        
        if (response.status === 401 || response.status === 403) {
            console.warn('Health endpoint reachable but backend rejected proxy credential (status:', response.status, ') — check server-side API_BASIC_AUTH.');
        }
        return true; // Got an HTTP response, so the server is running
    } catch (error) {
        console.warn('Server availability check failed:', error.message);
        return false;
    }
}

// Mock visitor data for when WebSocket is not available
const MOCK_DATA = {
    home: 3,
    rooms: 2,
    reservations: 1,
    payments: 0,
    hotels: 1
};

// Generate a unique client ID for this browser session
const CLIENT_ID = `client_${Math.random().toString(36).substring(2, 15)}`;

// Initialize the global WebSocket connection
function initializeGlobalWebSocket(onStatusChange) {
    // If already connected or connecting, don't create a new connection
    if (globalWs && (globalWs.readyState === WebSocket.OPEN || globalWs.readyState === WebSocket.CONNECTING)) {
        console.log('WebSocket already connected or connecting, reusing existing connection');
        return;
    }
    
    // If we're already in the process of connecting, don't start another connection
    if (isConnecting) {
        console.log('WebSocket connection already in progress');
        return;
    }
    
    isConnecting = true;
    console.log('Initializing global WebSocket connection');
    
    try {
        // Connect to the WebSocket proxy on this same server (server.js)
        // Credentials are injected server-side — never in the browser.
        const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
        const wsUrl = `${wsProtocol}://${window.location.host}/ws`;
        globalWs = new WebSocket(wsUrl);
        
        globalWs.onopen = () => {
            console.log('Global WebSocket connected successfully');
            isConnecting = false;
            
            // Notify all listeners of connection
            if (onStatusChange) onStatusChange('connected');
            
            // Send tracking messages for all registered endpoints
            for (const [endpoint, callbacks] of listeners.entries()) {
                sendTrackVisitor(endpoint);
            }
        };
        
        globalWs.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                console.log('WebSocket message received:', data);
                
                if (data.type === 'visitorUpdate') {
                    // Store the data for this endpoint
                    const endpoint = data.endpoint;
                    const count = data.data?.count || 0;
                    const total = data.data?.total || 0;
                    
                    console.log(`Visitor update for ${endpoint}: count=${count}, total=${total}`);
                    
                    endpointData.set('total', total);
                    
                    if (endpoint) {
                        endpointData.set(endpoint, count);
                    }
                    
                    // Notify all listeners that care about this endpoint
                    const callbacks = listeners.get(endpoint) || [];
                    callbacks.forEach(callback => callback(count, total));
                    
                    // Also notify listeners that care about the total
                    const totalCallbacks = listeners.get('total') || [];
                    totalCallbacks.forEach(callback => callback(null, total));
                } else if (data.type === 'initialCount') {
                    // Store all initial counts
                    console.log('Initial count data:', data.data);
                    
                    for (const [endpoint, count] of Object.entries(data.data || {})) {
                        endpointData.set(endpoint, count);
                    }
                    
                    // Calculate total
                    const total = Object.values(data.data || {}).reduce((a, b) => a + b, 0);
                    endpointData.set('total', total);
                    
                    // Notify all listeners
                    for (const [endpoint, callbacks] of listeners.entries()) {
                        const count = endpointData.get(endpoint) || 0;
                        callbacks.forEach(callback => callback(count, total));
                    }
                }
            } catch (error) {
                console.error('WebSocket message parsing error:', {
                    error: error.message,
                    data: typeof event.data === 'string' ? event.data.substring(0, 100) : 'non-string data'
                });
            }
        };
        
        globalWs.onerror = (event) => {
            console.warn('WebSocket error occurred:', {
                readyState: globalWs ? getReadyStateString(globalWs) : 'UNDEFINED',
                timestamp: new Date().toISOString(),
                browserInfo: {
                    userAgent: navigator.userAgent,
                    language: navigator.language,
                    online: navigator.onLine
                }
            });
            
            if (onStatusChange) onStatusChange('error');
        };
        
        globalWs.onclose = (event) => {
            console.log('Global WebSocket disconnected', {
                wasClean: event.wasClean,
                code: event.code,
                reason: event.reason || 'No reason provided'
            });
            
            isConnecting = false;
            globalWs = null;
            
            if (onStatusChange) onStatusChange('disconnected');
        };
    } catch (error) {
        console.error('Error creating WebSocket:', {
            message: error.message,
            stack: error.stack
        });
        
        isConnecting = false;
        globalWs = null;
        
        if (onStatusChange) onStatusChange('error');
    }
}

// Send track visitor message
function sendTrackVisitor(endpoint) {
    if (!globalWs || globalWs.readyState !== WebSocket.OPEN) {
        console.log(`Cannot track visitor for ${endpoint}: WebSocket not open`);
        return false;
    }
    
    try {
        const message = {
            type: 'trackVisitor',
            endpoint: endpoint,
            clientId: CLIENT_ID // Include client ID to distinguish between different browsers
        };
        
        globalWs.send(JSON.stringify(message));
        console.log(`Tracking visitor on ${endpoint} with client ID ${CLIENT_ID}`);
        return true;
    } catch (err) {
        console.error('Error sending track visitor message:', {
            message: err.message,
            endpoint: endpoint
        });
        return false;
    }
}

// Register a callback for a specific endpoint
function registerEndpointListener(endpoint, callback) {
    if (!listeners.has(endpoint)) {
        listeners.set(endpoint, []);
    }
    
    listeners.get(endpoint).push(callback);
    
    // If we already have data for this endpoint, call the callback immediately
    if (endpointData.has(endpoint)) {
        const count = endpointData.get(endpoint);
        const total = endpointData.get('total') || 0;
        callback(count, total);
    }
    
    // If WebSocket is open, send track visitor message
    if (globalWs && globalWs.readyState === WebSocket.OPEN) {
        sendTrackVisitor(endpoint);
    }
    
    // Return unregister function
    return () => {
        const callbacks = listeners.get(endpoint) || [];
        const index = callbacks.indexOf(callback);
        if (index !== -1) {
            callbacks.splice(index, 1);
        }
    };
}

export function useWebSocketVisitor(endpoint) {
    const [visitorCount, setVisitorCount] = useState(0);
    const [totalVisitors, setTotalVisitors] = useState(0);
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [serverAvailable, setServerAvailable] = useState(true);
    
    // Function to handle status changes
    const handleStatusChange = useCallback((status) => {
        setConnectionStatus(status);
    }, []);
    
    // Function to handle visitor updates
    const handleVisitorUpdate = useCallback((count, total) => {
        if (count !== null) setVisitorCount(count);
        if (total !== null) setTotalVisitors(total);
    }, []);
    
    // Initialize WebSocket connection and register listener
    useEffect(() => {
        // Check if browser supports WebSocket
        if (typeof WebSocket === 'undefined') {
            console.error('WebSocket is not supported in this browser');
            setServerAvailable(false);
            return;
        }
        
        // Check if browser is online
        if (!navigator.onLine) {
            console.log('Browser is offline. Will connect when online.');
            setConnectionStatus('offline');
            
            const handleOnline = () => {
                console.log('Browser is now online. Connecting WebSocket...');
                setConnectionStatus('connecting');
                initializeGlobalWebSocket(handleStatusChange);
            };
            
            window.addEventListener('online', handleOnline);
            return () => window.removeEventListener('online', handleOnline);
        }
        
        // Check server availability
        checkServerAvailability().then(isAvailable => {
            setServerAvailable(isAvailable);
            
            if (isAvailable) {
                // Initialize global WebSocket if needed
                initializeGlobalWebSocket(handleStatusChange);
                
                // Register listener for this endpoint
                const unregister = registerEndpointListener(endpoint, handleVisitorUpdate);
                
                // Update connection status based on current WebSocket state
                if (globalWs) {
                    switch (globalWs.readyState) {
                        case WebSocket.CONNECTING:
                            setConnectionStatus('connecting');
                            break;
                        case WebSocket.OPEN:
                            setConnectionStatus('connected');
                            break;
                        case WebSocket.CLOSING:
                        case WebSocket.CLOSED:
                            setConnectionStatus('disconnected');
                            break;
                    }
                }
                
                return unregister;
            } else {
                console.log('Server unavailable, using mock data');
                setConnectionStatus('unavailable');
                
                // Use mock data
                setVisitorCount(MOCK_DATA[endpoint] || 0);
                setTotalVisitors(Object.values(MOCK_DATA).reduce((a, b) => a + b, 0));
                
                return () => {};
            }
        });
    }, [endpoint, handleStatusChange, handleVisitorUpdate]);
    
    // Handle offline/online events
    useEffect(() => {
        const handleOffline = () => {
            console.log('Browser went offline. WebSocket connections may fail.');
            setConnectionStatus('offline');
        };
        
        window.addEventListener('offline', handleOffline);
        
        return () => {
            window.removeEventListener('offline', handleOffline);
        };
    }, []);
    
    // Function to manually reconnect
    const reconnect = useCallback(() => {
        console.log('Manual reconnection requested');
        
        // Close existing connection if any
        if (globalWs) {
            globalWs.close();
            globalWs = null;
        }
        
        // Reset connection status
        setConnectionStatus('connecting');
        
        // Check server availability again
        checkServerAvailability().then(isAvailable => {
            setServerAvailable(isAvailable);
            
            if (isAvailable) {
                // Initialize new WebSocket connection
                initializeGlobalWebSocket(handleStatusChange);
            } else {
                setConnectionStatus('unavailable');
            }
        });
    }, [handleStatusChange]);
    
    return { 
        visitorCount, 
        totalVisitors, 
        connectionStatus,
        serverAvailable,
        reconnect
    };
} 